import { useState, useEffect } from "react";
import { useI18n } from "./i18n";

/**
 * Loading screen shown briefly before the main menu.
 *
 * - Displays a title and a subtitle with the game's tagline
 * - Shows an animated progress bar that fills over ~3 seconds
 * - After the progress bar completes, auto-transitions to the main menu
 *   by calling `onReady`
 */
export interface LoadingScreenProps {
  /** 加载完成并应显示主菜单时调用。 */
  onReady: () => void;
}

/** 加载动画的持续时间（毫秒）。 */
const LOADING_DURATION_MS = 3000;

/**
 * The loading screen component.
 *
 * Renders a centered overlay with:
 *   - A pulsing game title
 *   - An animated progress bar (CSS keyframe-driven)
 *   - Contextual hint text that cycles through tips
 *   - Auto-calls `onReady` after `LOADING_DURATION_MS`
 */
export function LoadingScreen({ onReady }: LoadingScreenProps): JSX.Element {
  const [progress, setProgress] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const { t, tList } = useI18n();
  const tips = tList("loading_tips");

  useEffect(() => {
    // 进度条动画：每30ms递增一次，结束时达到100%。
    const stepMs = 30;
    const totalSteps = LOADING_DURATION_MS / stepMs;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setProgress(Math.min(100, Math.round((step / totalSteps) * 100)));
      if (step >= totalSteps) {
        clearInterval(timer);
        onReady();
      }
    }, stepMs);

    // 每800ms循环提示文本。
    const tipTimer = setInterval(() => {
      setTipIndex((i) => (i + 1) % 8);
    }, 800);

    return () => {
      clearInterval(timer);
      clearInterval(tipTimer);
    };
  }, [onReady]);

  return (
    <div className="loading-screen" data-testid="loading-screen">
      <div className="loading-content">
        <h1 className="loading-title">{t("loading_title")}</h1>
        <p className="loading-subtitle">{t("loading_subtitle")}</p>

        {/* Animated progress bar */}
        <div className="loading-progress-track" data-testid="loading-progress">
          <div
            className="loading-progress-fill"
            data-testid="loading-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="loading-progress-label" data-testid="loading-progress-label">
          {progress}%
        </span>

        {/* Rotating tip text */}
        <p className="loading-tip">{tips[tipIndex % Math.max(1, tips.length)] ?? ""}</p>
      </div>
    </div>
  );
}
